/* eslint-disable react/display-name */
import { CloseOutlined, DeleteOutlined, EditOutlined, FolderAddOutlined, FolderOutlined } from '@ant-design/icons';
import { Col, Row, Space } from 'antd';
import { always, any, cond, equals, filter, forEach, has, includes, map, not, path, pathOr, prop, propEq, propOr, T } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty, notEqual } from 'ramda-adjunct';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Components } from '@fasstech/flexiget';
import useDocumentsTable from '../../hooks/useDocumentsTable';
import DeleteWidgetsMutation from '../../_graphql/mutations/documents/DeleteWidgetsMutation';
import { useShowAction } from '../../hooks/useShowAction';
import ModalLoading from '../ModalLoading';
import OperationStatusSection from '../OperationStatusSection';
import RemoveFoldersMutation from '../../_graphql/mutations/folders/RemoveFoldersMutation';
import ConfirmButton from '../ConfirmButton';
import useUserRefreshLock from '../../hooks/useUserRefreshLock';
import useActionGuard from '../../hooks/useActionGuard';
import { errorMessage, successMessage } from '../../utils/messageMutation';
import { useTreeContext } from './treeContext';
import { updateKeys } from './treeHelper';

const TreeRow = memo(({ data, ElementRow, setElements, setRefetchWidgets }) => {
  const { addTree, updateTree, removeTree, expandedKeys, reFetchFolder, setExpandedKeys, setCheckedKeys } = useTreeContext();
  const { deleteDocuments, deleteRawDocuments } = useDocumentsTable();
  const [elementsTree, setElementsTree] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [errorRemoveFolder, setErrorRemoveFolder] = useState(false);
  const [statusRemoveFolder, setStatusRemoveFolder] = useState({
    step: 0,
    folder: false,
    items: false
  });

  const progress = statusRemoveFolder.step > 0 ? Math.floor(statusRemoveFolder.step * 100) : 0;

  const refEditable = useRef(null);
  const refRow = useRef(null);
  const { showAction } = useShowAction(refRow);
  const element = prop('element', data);
  const children = prop('children', data);
  const isFolder = propOr(false, 'isFolder', element);
  const allowEdit = propOr(false, 'allowEdit', element);
  const { elmId: elementId, name, treeId, allowAdd, allowDelete } = element;
  const hasParentFolder = isNotNilOrEmpty(prop('parent', data));
  const { lockUserRefresh, unlockUserRefresh } = useUserRefreshLock();
  const { loading, run } = useActionGuard();

  const getElements = (children) => {
    let elements = [];

    forEach(child => {
      const element = prop('element', child);
      const getProperty = (property) => path(['element', property], child);

      if (element) {
        const getType = (child) => {
          const isDocument = isNotNilOrEmpty(path(['element', 'pages'], child));
          const isFolder = getProperty('isFolder');
          const isWidget = has('contents', propOr({}, 'element', child));

          return cond([
            [() => isDocument, always('document')],
            [() => isFolder, always('folder')],
            [() => isWidget, always('widget')],
            [T, always('rawDocument')]
          ])();
        };

        elements.push({
          type: getType(child),
          elmId: getProperty('elmId'),
          treeId: getProperty('treeId'),
          parentId: getProperty('parent'),
          isUsed: getProperty('isUsed') || false,
          name: getProperty('name'),
          rootTreeId: hasParentFolder && children[0].parent.id
        });
      }
      const grandChild = pathOr([], ['children'], child);
      if (grandChild && grandChild.length > 0) {
        const childElements = getElements(grandChild);
        elements = elements.concat(childElements);
      }
    })(children);
    return elements;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setElementsTree(getElements(children)), [data]);

  const isUsedWidgets = any(propEq(true, 'isUsed'))(elementsTree);

  const handleDeleteElement = (elements, done) => {
    setIsOpen(true);
    lockUserRefresh();

    const documentsIds = [];
    const rawDocumentsIds = [];
    const widgetsIds = [];
    const foldersIds = [];

    forEach((element) => {
      const elmId = propOr('', 'elmId', element);
      const treeId = propOr('', 'treeId', element);
      cond([
        [equals('document'), () => {
          documentsIds.push(elmId);
          foldersIds.push(treeId);
        }],
        [equals('rawDocument'), () => {
          rawDocumentsIds.push(elmId);
          foldersIds.push(treeId);
        }],
        [equals('widget'), () => {
          widgetsIds.push(elmId);
          foldersIds.push(treeId);
        }
        ],
        [equals('folder'), () => foldersIds.push(treeId)]
      ])(prop('type', element));
      if (element.parentId && !foldersIds.includes(element.parentId)) {
        foldersIds.push(element.parentId);
      }
    })(elements);

    const TOTAL_NUMBER_STEPS = 5;
    const removeOfferStatus = (status = {}) => {
      setStatusRemoveFolder(prev => {
        const newStep = prop('step', prev) + 1 / TOTAL_NUMBER_STEPS;
        return {
          ...prev, ...status, step: newStep
        };
      });
    };

    const handleDeletion = (ids, deleteFunc) => new Promise((resolve) => {
      if (isNotNilOrEmpty(ids)) {
        deleteFunc(ids, (ok, error) => {
          if (error) setErrorRemoveFolder(true);
          removeOfferStatus();
          resolve();
        });
      } else {
        removeOfferStatus();
        resolve();
      }
    });

    const handleWidgetsDeletion = () => new Promise((resolve) => {
      if (isNotNilOrEmpty(widgetsIds)) {
        DeleteWidgetsMutation({ widgetsIds: widgetsIds }, (_ok, error) => {
          if (error) setErrorRemoveFolder(true);
          removeOfferStatus({ items: true });
          resolve();
        });
      } else {
        removeOfferStatus({ items: true });
        resolve();
      }
    });

    const handleFoldersDeletion = () => new Promise((resolve) => RemoveFoldersMutation({ foldersIds }, (_ok, error) => {
      if (error) setErrorRemoveFolder(true);
      removeOfferStatus({ folder: true });
      resolve();
    }));

    const deleteAll = async () => {
      let succeeded = false;
      try {
        removeOfferStatus();
        await Promise.all([
          handleFoldersDeletion(),
          handleDeletion(documentsIds, deleteDocuments),
          handleDeletion(rawDocumentsIds, deleteRawDocuments),
          handleWidgetsDeletion()
        ]);
        succeeded = true;
      } catch (error) {
        console.error(error);
        setErrorRemoveFolder(true);
      } finally {
        done(succeeded);
      }
    };
    deleteAll();
  };

  const getElementRow = useMemo(
    () =>
      React.cloneElement(ElementRow, {
        folder: data,
        elementId,
        refRow
      }),
    [ElementRow, data, elementId]
  );

  const handleBlur = () => {
    const text = refEditable.current.innerText;
    if (notEqual(name, text)) updateTree(treeId, expandedKeys)({ name: text });
  };

  const handleModalLoadingClick = () => {
    setIsOpen(false);
    const ids = map(propOr('', 'elmId'))(elementsTree);
    const newElements = filter((value) => not(includes(prop('elmId', value), ids)))(elementsTree);
    setElements(newElements);
    setRefetchWidgets(true);
    reFetchFolder();
    const nodeKey = prop('key', data);
    const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'remove');
    setExpandedKeys(updatedKeys);
    setCheckedKeys([]);
    setStatusRemoveFolder({
      step: 0,
      folder: false,
      items: false
    });
    unlockUserRefresh();
  };

  const deleteFolder = () => {
    const isEmptyFolder = isNilOrEmpty(children);
    return run(
      `delete-folder-${treeId}`,
      (done) => isEmptyFolder
        ? RemoveFoldersMutation({ foldersIds: [treeId] }, done)
        : handleDeleteElement(elementsTree, done),
      isEmptyFolder ? (ok, error) => {
        if (ok && !error) {
          successMessage('dossier', 'supprimé');
          reFetchFolder();
          setCheckedKeys([]);
        } else {
          errorMessage();
        }
      } : undefined
    );
  };

  return (
    <>
      <ModalLoading
        visible={isOpen}
        onClick={handleModalLoadingClick}
        progress={progress}
        infoLoading={(
          <OperationStatusSection
            operationType="la suppression du dossier et de ses éléments"
            items={[
              { label: 'Dossier supprimé', type: 'folder' },
              { label: 'Éléments du dossier supprimés', type: 'items' }
            ]}
            status={statusRemoveFolder}
            progress={progress}
            errorMsg="Impossible de supprimer correctement les éléments du dossier, veuillez contacter l'équipe Flexidocs"
            showError={equals(100, progress) && errorRemoveFolder}
          />
        )}
        textProgression={`${progress}%`}
      />
      <Row gutter={4} ref={refRow} title="">
        {!isFolder ? (
          getElementRow
        ) : (
          <>
            <Col>
              <FolderOutlined />
            </Col>

            <Col>
              <span
                ref={refEditable}
                className={`p-1 ${document.activeElement === refEditable.current ? 'outline-none border border-flexi-primary-color rounded-sm' : ''}`}
                suppressContentEditableWarning={true}
                contentEditable={allowEdit}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.target.blur();
                  }
                }}
                spellCheck={false}
              >
                {name}
              </span>
            </Col>
            <Col className={showAction ? 'opacity-100' : 'opacity-0'}>
              <Space>
                {hasParentFolder && allowEdit && (
                  <Components.Tooltip
                    title="Retirer le dossier de l'arborescence"
                  >
                    <CloseOutlined
                      onClick={() => updateTree(element.treeId, [])({ parent: null })}
                      className="btn-remove-folder-root text-black hover:text-flexi-grey"
                    />
                  </Components.Tooltip>
                )}
                {allowEdit && (
                  <Components.Tooltip title="Éditer">
                    <EditOutlined
                      onClick={() => {
                        refEditable.current.focus();
                      }}
                      className="btn-edit-folder hover:text-flexi-grey"
                    />
                  </Components.Tooltip>
                )}
                {allowAdd && (
                  <Components.Tooltip title="Ajouter un dossier">
                    <FolderAddOutlined
                      onClick={() => addTree(treeId, expandedKeys)()}
                      className="btn-add-folder hover:text-flexi-grey"
                    />
                  </Components.Tooltip>
                )}
                {allowDelete && (
                  <Components.Tooltip
                    title={
                      isUsedWidgets
                        ? 'Ce dossier contient un widget utilisé dans un document, vous ne pouvez pas le supprimer.'
                        : 'Supprimer le dossier'
                    }
                    onClick={() => isNilOrEmpty(children) && removeTree(treeId, data)}
                  >
                    <ConfirmButton
                      placement="topRight"
                      title="Êtes-vous sûr de vouloir supprimer ce dossier et son contenu ?"
                      onConfirm={deleteFolder}
                      disabled={isUsedWidgets}
                      disabledPopover={isNilOrEmpty(children)}
                      icon={<DeleteOutlined className="btn-remove-folder hover:text-red-300" />}
                      buttonType="text"
                      sizeButton="small"
                      className="!w-3.5 !h-3.5"
                      loading={propOr(false, `delete-folder-${treeId}`, loading)}
                    />
                  </Components.Tooltip>
                )}
              </Space>
            </Col>
          </>
        )}
      </Row>
    </>
  );
});

export default TreeRow;
